import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiUsers, FiTarget, FiDollarSign } = FiIcons;

const PerformanceOverview = ({ data }) => {
  // Use data passed from parent or fallback to safe defaults if undefined
  const safeData = data || {
    traffic: 0, conversions: 0, revenue: 0, conversionRate: 0,
    techHealth: 0, activeKeywords: 0, backlinks: 0,
    trafficTrend: []
  };

  const kpis = [
    {
      title: 'Total Traffic',
      value: safeData.traffic.toLocaleString(),
      trend: 'up',
      icon: FiUsers,
      color: 'text-blue-600'
    },
    {
      title: 'Conversions',
      value: safeData.conversions.toLocaleString(),
      trend: 'up',
      icon: FiTarget,
      color: 'text-green-600'
    },
    {
      title: 'Revenue',
      value: `$${safeData.revenue.toLocaleString()}`,
      trend: 'up',
      icon: FiDollarSign,
      color: 'text-purple-600'
    },
    {
      title: 'Conversion Rate',
      value: `${safeData.conversionRate}%`,
      trend: 'down',
      icon: FiTrendingUp,
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Performance Overview</h2>
        <p className="text-gray-600">Key metrics at a glance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <motion.div
            key={kpi.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg bg-opacity-10 flex items-center justify-center ${kpi.color.replace('text-', 'bg-')}`}>
                <SafeIcon icon={kpi.icon} className={`text-lg ${kpi.color}`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{kpi.value}</h3>
            <p className="text-sm text-gray-600">{kpi.title}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Traffic & Conversion Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={safeData.trafficTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px' 
              }} 
            />
            <Line 
              type="monotone" 
              dataKey="traffic" 
              stroke="#00A859" 
              strokeWidth={3}
              dot={{ fill: '#00A859', strokeWidth: 2, r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="conversions" 
              stroke="#002B60" 
              strokeWidth={3}
              dot={{ fill: '#002B60', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-gradient-to-br from-[#00A859] to-[#008a4a] rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">SEO Health Score</h4>
          <div className="text-3xl font-bold mb-1">{safeData.techHealth}/100</div>
          <p className="text-green-100">Technical Performance</p>
        </div>
        
        <div className="bg-gradient-to-br from-[#002B60] to-[#003d7a] rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Active Keywords</h4>
          <div className="text-3xl font-bold mb-1">{safeData.activeKeywords}</div>
          <p className="text-blue-100">Tracking rankings</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Backlinks</h4>
          <div className="text-3xl font-bold mb-1">{safeData.backlinks}</div>
          <p className="text-purple-100">Total Links</p>
        </div>
      </motion.div>
    </div>
  );
};

export default PerformanceOverview;